import React, { useCallback, useState } from 'react';
import {
    Flex,
    Button,
    Text,
    IconButton,
    Image,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useDisclosure,
    Box,
    Icon
} from '@salesforce/retail-react-app/app/components/shared/ui';


import { useBreakpointValue } from '@chakra-ui/react';

import { FormattedMessage } from 'react-intl';
import { HideOnMobile } from '@salesforce/retail-react-app/app/components/responsive';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {review} from '../../../integrations/bazarvoice/hooks/use-bazarvoice'
import Link from '@salesforce/retail-react-app/app/components/link'
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'

import {sendSubmitReviewEvent, sendViewErrorEvent} from '../../../utils/gtm-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSitePreferences, preferenceToBool} from '../../../hooks/use-site-preferences'

/* Forms */
import WriteReviewFields from '../../forms/write-review-fields'

/* Icons */
import { ChevronLeftIcon, CustomClose, CheckCircleIcon } from '../../icons';

/* Styles */
import {productThumbnailStyles, writeReviewCollectifBannerStyles} from './write-review.styles'

/* Constants */
const {app: appConfig} = getConfig()
const TRANSFORMATION_PARAMS = '?sw=100&q=75'
const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = appConfig?.PRODUCT_IMGS_RESIZE_PARAMS_ENABLED == 'true'

const LinkWithRegistration = withRegistration(Link)

const WriteReview = ({ reviewFormData, images, isOpen, onClose, product, writeReviewButtonRef }) => {
    const form = useForm({
        mode: 'onSubmit'
    });

    const { onOpen } = useDisclosure()
    const {data: customer} = useCurrentCustomer()
    const isRegistered = !!customer?.isRegistered
    const {data: pointsPrefs} = useSitePreferences(['enablePointsForReview'])
    const enablePointsForReview = preferenceToBool(pointsPrefs?.enablePointsForReview)

    const [isReviewSubmittedOpen, setReviewSubmittedOpen] = useState(false);
    const [isCloseDrawerOpen, setCloseDrawerOpen] = useState(false);
    const [wasCancelReviewClicked, setWasCancelReviewClicked] = useState(false); // New state variable

    const [globalError, setGlobalError] = useState(null);

    const drawerPlacement = useBreakpointValue({ base: 'bottom', md: 'right' });

    const {submitReview} = review();

    var pid = product?.id

    if (product?.type?.variant) {
        const matchingGroup = product.variationGroups.find(
            (group) => group?.variationValues?.color === product?.variationValues?.color
        )

        if (matchingGroup) {
            pid = matchingGroup.productId
        }
    }

    const onSubmit = async (data) => {
        try {
            const result = await submitReview({pid, formData: JSON.stringify(data)})
            if (result?.HasErrors) {
                Object.keys(result?.FormErrors?.FieldErrors).forEach((key) => {
                    const errorMsg = result?.FormErrors?.FieldErrors[key]?.Message
                    // GTM Modification
                    sendViewErrorEvent(errorMsg)
                    form.setError(key, {
                        type: 'manual',
                        message: errorMsg
                    })
                })
                return
            } else if (!result || result.hasError) {
                setGlobalError('Failed to submit data');
                return;
            }

            // GTM Modification
            sendSubmitReviewEvent(product, data, result?.SubmissionId)

            form.reset()
            setGlobalError(null)
            setReviewSubmittedOpen(true); // Open the review submitted modal
        } catch (error) {
            setGlobalError(error.message)
        }
    };

    const getImageUrl = useCallback((image) => {
        const baseUrl = image.disBaseLink || image.link
        return PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
            ? `${baseUrl}${TRANSFORMATION_PARAMS}`
            : baseUrl
    }, [])

    const handleEditReview = () => {
        setCloseDrawerOpen(false);
    };

    const handleCancelReview = () => {
        setCloseDrawerOpen(false); // Close the confirmation drawer
        setWasCancelReviewClicked(true);
        form.reset()
    };

    const handleDrawerSubmitComplete = () => {
        onClose();
        if (writeReviewButtonRef.current) {
            writeReviewButtonRef.current.focus();
        }
    };

    const handleDrawerCloseComplete = () => {
        if (wasCancelReviewClicked) {
            onClose();
            if (writeReviewButtonRef.current) {
                writeReviewButtonRef.current.focus();
            }
            setWasCancelReviewClicked(false); // Reset the state
        }
    };

    const handleWriteReviewClose = () => {
        setCloseDrawerOpen(true)
        if (wasCancelReviewClicked) {
            onClose();
            if (writeReviewButtonRef.current) {
                writeReviewButtonRef.current.focus();
            }
            setWasCancelReviewClicked(false); // Reset the state
        }
    }

    return (
        <>
            <Drawer
                placement="right"
                isOpen={isOpen}
                onClose={handleWriteReviewClose}
                onOpen={onOpen}
                size={{ base: 'full', md: 'sm' }}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <HideOnMobile>
                        <Flex alignItems={'center'} px={{ base: 6 }} mt={{ base: 6 }} mb={{ base: 4 }}>
                            <IconButton variant={'link'} onClick={handleWriteReviewClose} icon={<ChevronLeftIcon />} />
                            <Text fontSize={{ base: 'sm' }} lineHeight={{ base: '18px' }} ml={{ base: 2 }} cursor={'pointer'} onClick={handleWriteReviewClose}>
                                <FormattedMessage defaultMessage="Back" id="pdp_reviews.modal.back" />
                            </Text>
                        </Flex>
                    </HideOnMobile>

                    <IconButton
                        aria-label="Close"
                        icon={<CustomClose />}
                        position="absolute"
                        w={{ base: '24px' }}
                        h={{ base: '24px' }}
                        top={{ base: '32px', lg: '26px' }}
                        right={{ base: '16px', lg: '34px' }}
                        zIndex={'2'}
                        backgroundColor="transparent"
                        _hover={{ backgroundColor: 'transparent' }}
                        color="black"
                        onClick={() => setCloseDrawerOpen(true)}
                    />

                    <DrawerBody
                        px={{ base: 4, lg: 6 }}
                        py={{ base: 4 }}
                        sx={{ scrollbarGutter: { base: 'auto', lg: 'stable' } }}>
                        <form onSubmit={form.handleSubmit(onSubmit)}>
                            <Text fontSize={{base: 'md'}}
                                fontWeight={{base: 700}}
                                lineHeight={{base: '20px'}}
                                textTransform={{base: 'uppercase'}}
                                mt={{base: 4}}>
                                <FormattedMessage
                                    defaultMessage="Write your review"
                                    id="pdp_reviews.write.review.text"
                                />
                            </Text>
                            {enablePointsForReview && <Box {...writeReviewCollectifBannerStyles.container}>
                                <Text {...writeReviewCollectifBannerStyles.title}>
                                    {isRegistered ? (
                                        <FormattedMessage
                                            id="pdp_reviews.collectif_banner.logged_in.title"
                                            defaultMessage="Leave a Review, Get Spoiled"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="pdp_reviews.collectif_banner.guest.title"
                                            defaultMessage="Join the GARAGE Collectif & Get Spoiled"
                                        />
                                    )}
                                </Text>
                                <Text {...writeReviewCollectifBannerStyles.body}>
                                    {isRegistered ? (
                                        <FormattedMessage
                                            id="pdp_reviews.collectif_banner.logged_in.body"
                                            defaultMessage="Get 10 points when you drop a review via our website and 10 bonus points if you upload a photo, too!"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="pdp_reviews.collectif_banner.guest.body"
                                            defaultMessage="Members get 10 points for every review they leave via our website and another 10 bonus points if they uploaded a photo. <joinLink>Join now</joinLink> & start racking up points!"
                                            values={{
                                                joinLink: (chunks) => (
                                                    <Link
                                                        href="/registration"
                                                        {...writeReviewCollectifBannerStyles.link}
                                                    >
                                                        {chunks}
                                                    </Link>
                                                )
                                            }}
                                        />
                                    )}
                                </Text>
                            </Box>}

                            <WriteReviewFields
                                pid={pid}
                                form={form}
                                reviewFormData={reviewFormData}
                                product={product}
                            >
                                {images?.length > 0 && (
                                    <Box
                                        {...productThumbnailStyles.container}
                                    >
                                        <Image
                                            src={getImageUrl(images[0])}
                                            alt={images[0].alt}
                                            title={images[0]?.title}
                                            {...productThumbnailStyles.image}
                                        />
                                    </Box>
                                )}
                            </WriteReviewFields>

                            <Button type="submit"
                                title="Submit"
                                textTransform={'uppercase'}
                                mt={8}
                                w={'100%'}
                                minH={'48px'}>
                                <FormattedMessage
                                    defaultMessage="Submit review"
                                    id="pdp_reviews.write.review.submit"
                                />
                            </Button>

                            {globalError && <Text color="red.600" mt={2}>{globalError}</Text>}

                            <Text fontSize={{base: 'xs'}} mt={4}>
                                <FormattedMessage
                                    defaultMessage="All submitted reviews are subject to the terms set forth in our website Conditions of Use. If your review is in violation of our Ratings Guidelines, it will be rejected."
                                    id="pdp_reviews.terms.notice"
                                />
                            </Text>
                        </form>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Review Submitted Confirmation Drawer */}
            <Drawer
                placement="right"
                isOpen={isReviewSubmittedOpen}
                onClose={() => setReviewSubmittedOpen(false)}
                onOpen={isReviewSubmittedOpen}
                onCloseComplete={handleDrawerSubmitComplete}
                size={{ base: 'full', md: 'sm' }}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerCloseButton
                        w={{ base: 6 }}
                        h={{ base: 6 }}
                        top={{ base: '32px', lg: '26px' }}
                        right={{ base: '16px', lg: '34px' }}
                        zIndex={'2'}
                    />
                    <DrawerBody
                        px={{ base: 4, lg: 6 }}
                        py={{ base: 4 }}
                        sx={{ scrollbarGutter: { base: 'auto', lg: 'stable' } }}
                    >
                        <Icon as={CheckCircleIcon} width="38px" height="38px" marginTop="32px" />

                        <Text fontSize={{base: '20px'}}
                            fontWeight={{base: 700}}
                            lineHeight={{base: '20px'}}
                            textTransform={{base: 'uppercase'}}
                            mt={{base: "24px"}}>
                            <FormattedMessage
                                defaultMessage="YOUR REVIEW WAS SUBMITTED SUCCESSFULLY!"
                                id="pdp_reviews.review.submitted.successfully"
                            />
                        </Text>

                        <Text
                            fontSize={{base: '14px'}}
                            lineHeight={{base: '18px'}}
                            mt={{base: 2}}
                        >
                            <FormattedMessage
                                defaultMessage="Please note that it may take up to 72 hours for your review to appear."
                                id="pdp_reviews.review.submitted.description"
                            />
                        </Text>

                        <Button
                            fontSize={'md'}
                            onClick={() => setReviewSubmittedOpen(false)}
                            mt="48px"
                            w="100%"
                        >
                            <FormattedMessage
                                defaultMessage="Continue Shopping"
                                id="pdp_reviews.continue_shopping.button"
                            />
                        </Button>
                        <HideOnMobile>
                            <Button
                                variant="outline"
                                fontSize={'md'}
                                onClick={() => setReviewSubmittedOpen(false)}
                                mt={2}
                                w="100%"
                            >
                                <FormattedMessage
                                    defaultMessage="Back to reviews"
                                    id="pdp_reviews.back_to_reviews.button"
                                />
                            </Button>
                        </HideOnMobile>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Close Drawer Confirmation */}
            <Drawer
                placement={drawerPlacement}
                isOpen={isCloseDrawerOpen}
                onClose={() => setCloseDrawerOpen(false)}
                size={{ base: 'sm', md: 'sm' }}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={drawerPlacement === 'right'}
                onCloseComplete={handleDrawerCloseComplete}
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerCloseButton
                        w={{ base: 6 }}
                        h={{ base: 6 }}
                        top={{ base: '32px', lg: '26px' }}
                        right={{ base: '16px', lg: '34px' }}
                        zIndex={'2'}
                    />
                    <DrawerBody
                        px={{ base: 4, lg: 6 }}
                        py={{ base: 4 }}
                        sx={{ scrollbarGutter: { base: 'auto', lg: 'stable' } }}
                    >
                        <Text fontSize={{base: '20px'}}
                            fontWeight={{base: 700}}
                            lineHeight={{base: '20px'}}
                            textTransform={{base: 'uppercase'}}
                            mt={{base: "32px", md: "64px"}}
                            pt={{base: "24px"}}>
                            <FormattedMessage
                                defaultMessage="ARE YOU SURE YOU WANT TO QUIT?"
                                id="pdp_reviews.quit.review.question"
                            />
                        </Text>

                        <Text
                            fontSize={{base: '14px'}}
                            lineHeight={{base: '18px'}}
                            mt={{base: 2}}
                        >
                            <FormattedMessage
                                defaultMessage="Are you sure you want to cancel the review process? Your progress will not be saved."
                                id="pdp_reviews.quit.review.description"
                            />
                        </Text>

                        <Flex gap={2} direction={'column'} mt={{base: '48px'}} mb={{base: '24px'}}>
                            <Button type="button" fontSize="{{base: '16px'}}" colorScheme="blue" onClick={handleCancelReview}>
                                <FormattedMessage
                                    defaultMessage="CANCEL REVIEW"
                                    id="pdp_reviews.cancel.review.button"
                                />
                            </Button>
                            <Button type="button" variant="outline" onClick={handleEditReview}>
                                <FormattedMessage
                                    defaultMessage="EDIT MY REVIEW"
                                    id="pdp_reviews.edit.review.button"
                                />
                            </Button>
                        </Flex>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
};

WriteReview.propTypes = {
    product: PropTypes.object.isRequired,
    reviewFormData: PropTypes.object,
    images: PropTypes.array,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
};

export default WriteReview;
